import Vue from 'vue';
import { baseUrl } from '../services/api';
import WarehouseTree from '../../components/partials/modals/Tool/Warehouse/Warehouse.vue';
import { getCookieByName, isMobileBrowser } from './util';
import {
  CONTENT_DOWNLOAD_PATH,
  downloadContentFile,
  fetchContentBlobUrl,
  isContentDownloadUrl,
  toStoredContent,
} from '../services/contentFile';

// Content/DownloadFile authenticated olduğu için tarayıcının bu URL'leri kendi başına
// çekmesine izin veremiyoruz. Gerçek adres data-cfm-src / data-cfm-href'te saklanır, byte'lar
// authenticated fetch ile alınır: görseller blob: URL olarak basılır, dosya linkleri tıklamada
// indirilir. Kaydetme anında gerçek adresler geri yazılır (restoreOriginalContentUrls).
const CONTENT_IMG_SELECTOR = `img[data-cfm-src], img[src*="${CONTENT_DOWNLOAD_PATH}"]`;
const CONTENT_LINK_SELECTOR = `a[data-cfm-href], a[href*="${CONTENT_DOWNLOAD_PATH}"]`;

// Container başına: üretilen blob URL'ler + uçuşta olan indirmeler. Kapanışta DOM'u taramak
// kullanıcının editörde silip değiştirdiği görsellerin blob'larını kaçırıp sekme ömrü boyunca
// sızdırırdı, bu yüzden ayrı bir kayıt tutuyoruz. Dedup bilinçli olarak container'a özel:
// blob URL'i iki popup arasında paylaşmak, biri kapanıp revoke ettiğinde diğerini bozardı.
const containerContentState = new WeakMap();

function contentStateOf(container) {
  let state = containerContentState.get(container);
  if (!state) {
    state = { blobUrls: new Set(), inFlight: new Map() };
    containerContentState.set(container, state);
  }
  return state;
}

// Aynı görsel iki kez istenirse (ör. redactor img'yi yeniden DOM'a eklediğinde) tek indirme
// yapılır ve öksüz blob URL kalmaz.
function contentBlobUrl(state, url) {
  let pending = state.inFlight.get(url);
  if (!pending) {
    pending = fetchContentBlobUrl(url).finally(() => state.inFlight.delete(url));
    state.inFlight.set(url, pending);
  }
  return pending;
}

function releaseBlobUrls(container) {
  const state = containerContentState.get(container);
  if (!state) return;
  state.blobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
  containerContentState.delete(container);
}

// blob: src "zaten indirildi" demek; img yeniden DOM'a eklenirse tekrar indirilmez.
async function rehydrateContentImage(container, img) {
  if (img.src.startsWith('blob:')) return;
  const realSrc = img.dataset.cfmSrc || img.getAttribute('src');
  if (!isContentDownloadUrl(realSrc)) return;
  const state = contentStateOf(container);
  try {
    const blobUrl = await contentBlobUrl(state, realSrc);
    img.dataset.cfmSrc = realSrc;
    img.src = blobUrl;
    state.blobUrls.add(blobUrl);
  } catch (error) {
    console.error('Content görseli indirilemedi:', error);
  }
}

function rehydrateContentImages(container, root = container) {
  root.querySelectorAll(CONTENT_IMG_SELECTOR).forEach((img) => rehydrateContentImage(container, img));
}

// Dosya linki tıklaması: native navigasyon header koyamaz (ctrl+click / orta tık dahil), bu
// yüzden href sanitize sırasında javascript:; yapılır ve indirme buradan yürür. Aynı desen
// projede Summernote entegrasyonunda da var (summernote.js .download-link).
function handleContentLinkClick(event) {
  const anchor = event.target.closest(CONTENT_LINK_SELECTOR);
  if (!anchor) return;
  const realHref = anchor.dataset.cfmHref || anchor.getAttribute('href');
  if (!isContentDownloadUrl(realHref)) return;
  event.preventDefault();
  downloadContentFile(realHref, anchor.textContent && anchor.textContent.trim())
    .catch((error) => {
      console.error('Content dosyası indirilemedi:', error);
      toastr.error('Dosya indirilirken bir hata oluştu.'); // TODO - Localization
    });
}

// Hem redactor('set', ...) render'ını hem kullanıcının editöre yeni resim yüklediği anı
// yakalar; ikisi de aynı childList mutation'ı.
function watchContentImages(container) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(CONTENT_IMG_SELECTOR)) rehydrateContentImage(container, node);
        rehydrateContentImages(container, node);
      });
    }
  });
  observer.observe(container, { childList: true, subtree: true });
  return observer;
}

function attachContentHandlers(source, container, { observe = false } = {}) {
  source.cfmContentContainer = container;
  container.addEventListener('click', handleContentLinkClick);
  if (observe) source.cfmContentObserver = watchContentImages(container);
}

// Idempotent: close ve destroy yolları ikisi de çağırabilir (mobil save/cancel doğrudan
// destroy() çağırıp beforeclose'u atlıyor, bkz. redactor save/cancel callback'leri).
function detachContentHandlers(source) {
  const container = source.cfmContentContainer;
  if (!container) return;
  source.cfmContentContainer = null;
  source.cfmContentObserver?.disconnect();
  source.cfmContentObserver = null;
  container.removeEventListener('click', handleContentLinkClick);
  releaseBlobUrls(container);
}

// DOMParser + body.innerHTML bir round-trip'tir: markup'ı normalize eder ve <head>'e taşınan
// içeriği (ör. baştaki bir <style>) düşürür - bu yüzden dönüştürülecek işaret yoksa string'e
// hiç dokunmuyoruz. DOMParser inert'tir: ağa çıkmaz, script çalıştırmaz.
function mapHtml(html, markers, transform) {
  if (!html || !markers.some((marker) => html.includes(marker))) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  transform(doc);
  return doc.body.innerHTML;
}

function moveAttribute(element, from, to, replacement) {
  element.setAttribute(to, element.getAttribute(from));
  if (replacement === null) element.removeAttribute(from);
  else element.setAttribute(from, replacement);
}

// Ham HTML CANLI DOM'a basılmadan ÖNCE çağrılmalı: bir <img> DOM'a bağlandığı anda tarayıcı
// src'sini çeker ve sonradan blob'a çevirmek o authenticated olmayan isteği geri alamaz.
function sanitizeContentUrls(html) {
  return mapHtml(html, [CONTENT_DOWNLOAD_PATH], (doc) => {
    doc.querySelectorAll('img[src]').forEach((img) => {
      if (isContentDownloadUrl(img.getAttribute('src'))) moveAttribute(img, 'src', 'data-cfm-src', null);
    });
    doc.querySelectorAll('a[href]').forEach((anchor) => {
      if (isContentDownloadUrl(anchor.getAttribute('href'))) moveAttribute(anchor, 'href', 'data-cfm-href', 'javascript:;');
    });
  });
}

// code.get() canlı DOM'un serileştirilmiş hâlini döner; blob: URL'ler kalıcı olmadığı için
// DB'ye yazılmadan önce gerçek adresler geri yazılır.
function restoreOriginalContentUrls(html) {
  return mapHtml(html, ['data-cfm-src', 'data-cfm-href'], (doc) => {
    doc.querySelectorAll('img[data-cfm-src]').forEach((img) => moveAttribute(img, 'data-cfm-src', 'src', null));
    doc.querySelectorAll('a[data-cfm-href]').forEach((anchor) => moveAttribute(anchor, 'data-cfm-href', 'href', null));
  });
}

export const Editor = (props, ref) => {
  if (ref && !ref.isDestroyed) ref.close();
  if (props === null) return null;
  if (props.type === 'popupHtml') {
    ref = new bryntum.scheduler.Popup({
      refElement: props.refElement,
      maximize: false,
      maxHeight: '100vh',
      draggable: { handleSelector: ':not(.b-popup-content)' },
      scrollable: true,
      centered: true,
      showOnclick: true,
      closable: true,
      width: 946,
      autoClose: false,
      closeAction: 'destroy',
      autoShow: false,
      trapFocus: true,
      html: '<textarea class="redactor" placeholder="..."></textarea>',
      tools: {
        info: {
          html: bryntum.scheduler.LocaleManager.locale.Popup.counter(),
          style: 'font-size:9px!important;margin-right:9px',
          weight: -1000,
        },
        done: {
          cls: 'b-fa-check-square b-hidden',
          style: 'margin-right:10px',
          handler: async (e) => {
            await onPopupHtmlDone(props, ref);
          },
          weight: -1000,
        },
      },
      onPaint: ({ source }) => {
        const lang = localStorage.getItem('vue-lang');
        source.tools.done.element.classList.remove('b-hidden');
        attachContentHandlers(source, source.bodyElement, { observe: true });
        $(source.bodyElement.querySelector('.redactor')).redactor({
          forceFullscreen: isMobileBrowser(),
          save: async () => {
            if (isMobileBrowser()) {
              await onPopupHtmlDone(props, ref);
              !ref.isDestroyed && ref.destroy();
            }
          },
          cancel: async () => {
            if (isMobileBrowser()) {
              !ref.isDestroyed && ref.destroy();
            }
          },
          imageUpload: `${baseUrl}/File/SaveContent`,
          fileUpload: `${baseUrl}/File/SaveContent`,
          requestHeaders: [{
            key: 'Authorization',
            value: `${getCookieByName('tokenType')} ${getCookieByName('token')}`,
          }, {
            key: 'Culture',
            value: localStorage.getItem('vue-lang'),
          }],
          imageUploadFields: {
            baseUrl,
          },
          fileUploadFields: {
            baseUrl,
          },
          buttons: ['format', 'bold', 'italic', 'deleted', 'underline', 'horizontalrule', 'lists', 'link', 'file', 'image'],
          plugins: ['undoredo', 'inlinestyle', 'fontfamily', 'fontsize', 'fontcolor', 'alignment', 'table', 'fullscreen', 'imageUpload', 'fileUpload', 'counter'], // 'video', //todo static id on video plugin
          imagePosition: true,
          imageResizable: true,
          tabAsSpaces: 4,
          minHeight: 350,
          maxHeight: 350,
          dragUpload: true,
          multipleUpload: true,
          clipboardUpload: true,
          lang: lang === 'tr-Tr' ? lang : 'en',
          callbacks: {
            uploadError: (message) => toastr.error(message),
            counter: (data) => {
              source.tools.info.element.innerText = bryntum.scheduler.LocaleManager.locale.Popup.counter(data);
            },
          },
        });
      },
      onBeforeClose: ({ source }) => {
        detachContentHandlers(source);
        $(source.bodyElement.querySelector('.redactor')).redactor('core.destroy'); // todo check if work
      },
      onBeforeDestroy: ({ source }) => detachContentHandlers(source),
    });
    ref.show();
    ref.contentElement.style.overflow = 'inherit';
    props.value && $(ref.bodyElement.querySelector('.redactor')).redactor('set', sanitizeContentUrls(props.value)); // todo check if work
    return ref;
  } if (props.type === 'popupVue') {
    let instance = null;
    const a = Vue.extend(props.vueComponent);
    instance = new a({
      propsData: {
        ...props.vueProps,
      },
    });

    if (props.vueEvents) {
      for (const event of props.vueEvents) {
        instance.$on(event.name, event.callback);
      }
    }

    ref = new bryntum.scheduler.Popup({
      forElement: '', // props.refElement,
      maximize: false,
      maxHeight: '100vh',
      draggable: { handleSelector: ':not(.b-popup-content)' },
      scrollable: true,
      anchor: true,
      showOnclick: true,
      closable: true,
      width: 946,
      autoClose: false,
      closeAction: 'hide',
      autoShow: false,
      trapFocus: false,
      html: '',
      style: 'background-color:white',
      /* listeners: {
                catchAll: function (event) {
                    console.log(event.type, event);
                },
                thisObj: this
            }, */
      changeProp: (newProps) => {
        if (newProps) {
          for (const propKey in newProps) {
            Vue.set(instance, propKey, newProps[propKey]);
          }
        }
      },
      /* tools: {
                done: {
                    cls: 'b-fa-check-square',
                    style: 'margin-right:10px',
                    handler: async () => {
                        ref.mask(bryntum.scheduler.LocaleManager.locale.Grid.loadMask);
                        props.value = value;
                        await props.callback(props.value);
                        !ref.isDestroyed && ref.unmask();
                    },
                    weight: -1000,
                },
            }, */
      onPaint: ({ source }) => {
        instance.$mount(source.contentElement);
      },
      onBeforeClose: ({ source }) => {
        // instance.$destroy();
        // instance.$el.remove();
      },
      onBeforeDestroy: ({ source }) => {
        instance.$el.remove();
        instance.$destroy();
      },
    });
    // ref.show();
    ref.contentElement.style.overflow = 'inherit';
    return ref;
  }
  if (props.type === 'toolTip') {
    ref = new bryntum.scheduler.Popup({
      refElement: props.refElement,
      maximize: false,
      draggable: { handleSelector: ':not(.b-popup-content)' },
      scrollable: true,
      centered: true,
      showOnclick: true,
      closable: true,
      width: 500,
      minHeight: 100,
      maxHeight: '100vh',
      autoClose: false,
      closeAction: 'destroy',
      autoShow: false,
      trapFocus: true,
      onPaint: async ({ source }) => {
        source.title = '';
        source.html = '';
        // Kayıtlı içeriği basmanın TEK doğru yolu: sanitize'ı popup sahipleniyor, böylece
        // çağıran (props.onPaint) source.html'e ham HTML yazıp adımı atlayamaz.
        source.setContentHtml = (html) => { source.html = sanitizeContentUrls(html); };
        source.mask(bryntum.scheduler.LocaleManager.locale.Grid.loadMask);
        await props.onPaint(source);
        rehydrateContentImages(source.contentElement);
        attachContentHandlers(source, source.contentElement);
        source.unmask();
      },
      onBeforeClose: ({ source }) => detachContentHandlers(source),
      onBeforeDestroy: ({ source }) => detachContentHandlers(source),
      tools: {
        delete: {
          cls: 'b-fa-comment-times',
          style: 'display:none; margin-right:10px; color:red',
          handler: async () => {
            ref.mask(bryntum.scheduler.LocaleManager.locale.Grid.loadMask);
            await props.callback(ref);
            !ref.isDestroyed && ref.unmask();
          },
          weight: -1000,
        },
        maximize: {
          cls: 'b-fa-minus-square',
          handler: () => {
            ref.maximize = !ref.maximize;
            ref.tempHeight = ref.maximize && ref.height || ref.tempHeight;
            ref.tempWidth = ref.maximize && ref.width || ref.tempWidth;
            ref.tempX = ref.maximize && ref.x || ref.tempX;
            ref.tempY = ref.maximize && ref.y || ref.tempY;
            ref.x = ref.maximize ? 0 : ref.tempX;
            ref.y = ref.maximize ? 0 : ref.tempY;
            ref.width = ref.maximize ? '100%' : ref.tempWidth;
            ref.height = ref.maximize ? '100%' : ref.tempHeight;
            ref.draggable = !ref.maximize ? { handleSelector: ':not(.b-popup-content)' } : false;
          },
          weight: -1000,
        },

      },
    });
    ref.show();
    ref.contentElement.style.display = 'block';
    return ref;
  }
};

async function onPopupHtmlDone(props, ref) {
  ref.mask(bryntum.scheduler.LocaleManager.locale.Grid.loadMask);
  let value = $(ref.bodyElement.querySelector('.redactor')).redactor('code.get');
  if (!value) {
    // TODO - Localization
    toastr.error(bryntum.scheduler.LocaleManager.locale.Popup.enterValue);
    !ref.isDestroyed && ref.unmask();
    return;
  }
  // Canlı DOM'da gösterim için blob: src'ye çevrilen resimler, DB'ye yazılmadan önce
  // gerçek Content/DownloadFile adresine geri döndürülür (blob: URL kalıcı değildir).
  value = restoreOriginalContentUrls(value);
  // Base url placeholder'a çevrilir; kurallar toStoredContent'te.
  value = toStoredContent(value);
  props.value = value;
  await props.callback(props.value);
  !ref.isDestroyed && ref.unmask();
}
